export const CLOUD_COLLECTIONS = [
  "activity",
  "items",
  "tasks",
  "taskGroups",
  "taskLists",
  "taskSteps",
  "taskAttachments",
  "habits",
  "habitLogs",
  "habitWeeklyReviews",
  "habitNames",
  "habitKinds",
  "habitSettings",
  "personalTransactions",
  "goals",
  "fitnessLogs",
  "workouts",
  "progressPhotos",
  "reminders",
  "weeklySupplementCompletions",
  "dailyReviews",
  "weeklyReviews",
  "routines",
  "sleepLogs",
  "journalEntries",
  "monthlyResets",
  "workArchive",
  "gamification",
  "settings",
  "meta",
] as const;

export type CloudCollection = (typeof CLOUD_COLLECTIONS)[number];

export type FlatRecord = Readonly<{
  collection: CloudCollection;
  recordId: string;
  payload: unknown;
  clientUpdatedAt: number;
  contentHash: string;
}>;

export type HashIndex = Record<
  string,
  Readonly<{ contentHash: string; serverRevision: number }>
>;

export type CloudMutation = Readonly<{
  mutationId: string;
  collection: CloudCollection;
  recordId: string;
  operation: "upsert" | "delete";
  baseRecordRevision: number;
  clientUpdatedAt: number;
  contentHash: string;
  payload?: unknown;
}>;

export type ServerChange = Readonly<{
  ownerUid: string;
  collection: CloudCollection;
  recordId: string;
  payload: unknown | null;
  clientUpdatedAt: number;
  contentHash: string;
  deletedAt: number | null;
  serverRevision: number;
}>;

export type LegacyState = Record<string, unknown> & {
  deleted?: Record<string, number>;
  settings?: Record<string, unknown>;
  habitLogs?: unknown[];
  habitCompletions?: Record<string, Record<string, string>>;
  tasks?: unknown[];
};

type Identity = (row: Readonly<Record<string, unknown>>) => string;

const id = (row: Readonly<Record<string, unknown>>): string => String(row.id ?? "");
const date = (row: Readonly<Record<string, unknown>>): string => String(row.date ?? "");
const week = (row: Readonly<Record<string, unknown>>): string => String(row.week ?? "");

const ARRAY_IDENTITIES: Readonly<Partial<Record<CloudCollection, Identity>>> = {
  activity: id,
  items: id,
  tasks: id,
  taskGroups: id,
  taskLists: id,
  taskSteps: id,
  taskAttachments: id,
  habits: id,
  habitLogs: (row) => `${String(row.habitId ?? row.id ?? "")}|${String(row.date ?? "")}`,
  habitWeeklyReviews: (row) => String(row.weekStart ?? ""),
  personalTransactions: id,
  goals: id,
  fitnessLogs: date,
  workouts: id,
  progressPhotos: id,
  reminders: id,
  dailyReviews: date,
  weeklyReviews: week,
  routines: id,
  sleepLogs: date,
  journalEntries: date,
  monthlyResets: (row) => String(row.month ?? row.date ?? ""),
};

const SINGLETON_COLLECTIONS: readonly CloudCollection[] = [
  "habitNames",
  "habitKinds",
  "habitSettings",
  "weeklySupplementCompletions",
  "workArchive",
  "gamification",
  "settings",
  "meta",
];

const SETTINGS_LOCAL_ONLY = [
  "syncHealth",
  "syncConflicts",
  "dataHealth",
  "photoStorage",
  "hasExported",
] as const;

function plainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function timestamp(value: unknown, fallback = 0): number {
  if (!plainObject(value)) return fallback;
  const numeric = [value.updated, value.savedAt, value.created]
    .map(Number)
    .find((candidate) => Number.isFinite(candidate) && candidate > 0);
  if (numeric !== undefined) return numeric;
  for (const field of ["updatedAt", "savedAt", "createdAt", "completedAt"] as const) {
    if (typeof value[field] !== "string") continue;
    const parsed = Date.parse(value[field]);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!plainObject(value)) return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .filter((key) => value[key] !== undefined)
      .map((key) => [key, stableValue(value[key])]),
  );
}

function jsonClone(value: unknown): unknown {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) throw new TypeError("Cloud records must be JSON serializable");
  return JSON.parse(serialized);
}

export function canonicalJson(value: unknown): string {
  return JSON.stringify(stableValue(jsonClone(value)));
}

export async function contentHash(value: unknown): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonicalJson(value)),
  );
  const hex = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `sha256:${hex}`;
}

function sanitizeSettings(value: unknown): Record<string, unknown> {
  const settings = plainObject(jsonClone(value))
    ? (jsonClone(value) as Record<string, unknown>)
    : {};
  for (const key of SETTINGS_LOCAL_ONLY) delete settings[key];
  for (const key of ["habitReminder", "supplementReminder"] as const) {
    const reminder = settings[key];
    if (!plainObject(reminder)) continue;
    delete reminder.permission;
    delete reminder.lastNotifiedDate;
  }
  return settings;
}

function sanitizePayload(collection: CloudCollection, value: unknown): unknown {
  if (collection === "settings") return sanitizeSettings(value);
  if (collection === "meta" && plainObject(value)) {
    const meta = { ...value };
    delete meta.writerId;
    delete meta.revision;
    return jsonClone(meta);
  }
  if (collection === "progressPhotos" && plainObject(value)) {
    const photo = { ...value };
    delete photo.dataUrl;
    return jsonClone(photo);
  }
  return jsonClone(value);
}

export async function flattenState(state: LegacyState): Promise<FlatRecord[]> {
  const fallback = timestamp(state.meta, Date.now());
  const pending: Array<{
    collection: CloudCollection;
    recordId: string;
    payload: unknown;
    clientUpdatedAt: number;
  }> = [];

  for (const collection of CLOUD_COLLECTIONS) {
    const identity = ARRAY_IDENTITIES[collection];
    if (identity === undefined) continue;
    const rows = state[collection];
    if (!Array.isArray(rows)) continue;
    for (const candidate of rows) {
      if (!plainObject(candidate)) continue;
      const recordId = identity(candidate);
      if (recordId.length === 0 || recordId === "|") continue;
      pending.push({
        collection,
        recordId,
        payload: sanitizePayload(collection, candidate),
        clientUpdatedAt: timestamp(candidate, fallback),
      });
    }
  }

  for (const collection of SINGLETON_COLLECTIONS) {
    if (state[collection] === undefined) continue;
    const payload = sanitizePayload(collection, state[collection]);
    pending.push({
      collection,
      recordId: "singleton",
      payload,
      clientUpdatedAt: timestamp(payload, fallback),
    });
  }

  return Promise.all(
    pending.map(async (record) => ({
      ...record,
      contentHash: await contentHash(record.payload),
    })),
  );
}

export function recordIndexKey(collection: CloudCollection, recordId: string): string {
  return `${collection}\u0000${recordId}`;
}

export function buildMutations(
  records: readonly FlatRecord[],
  tombstones: Readonly<Record<string, number>>,
  acknowledged: Readonly<HashIndex>,
  mutationId: () => string = () => crypto.randomUUID(),
): CloudMutation[] {
  const changes: CloudMutation[] = [];
  for (const record of records) {
    const prior = acknowledged[recordIndexKey(record.collection, record.recordId)];
    if (prior?.contentHash === record.contentHash) continue;
    changes.push({
      mutationId: mutationId(),
      collection: record.collection,
      recordId: record.recordId,
      operation: "upsert",
      baseRecordRevision: prior?.serverRevision ?? 0,
      clientUpdatedAt: record.clientUpdatedAt,
      contentHash: record.contentHash,
      payload: record.payload,
    });
  }

  const allowed = new Set<string>(CLOUD_COLLECTIONS);
  for (const [key, deletedAt] of Object.entries(tombstones)) {
    const separator = key.indexOf(":");
    if (separator <= 0 || !Number.isFinite(deletedAt) || deletedAt <= 0) continue;
    const collection = key.slice(0, separator);
    const recordId = key.slice(separator + 1);
    if (!allowed.has(collection) || recordId.length === 0) continue;
    const typedCollection = collection as CloudCollection;
    const prior = acknowledged[recordIndexKey(typedCollection, recordId)];
    const deletionHash = `tombstone:${deletedAt}`;
    if (prior?.contentHash === deletionHash) continue;
    changes.push({
      mutationId: mutationId(),
      collection: typedCollection,
      recordId,
      operation: "delete",
      baseRecordRevision: prior?.serverRevision ?? 0,
      clientUpdatedAt: deletedAt,
      contentHash: deletionHash,
    });
  }
  return changes;
}

function preserveSettingsLocalFields(
  local: Record<string, unknown>,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...local, ...incoming };
  for (const key of SETTINGS_LOCAL_ONLY) {
    if (local[key] !== undefined) merged[key] = local[key];
  }
  for (const key of ["habitReminder", "supplementReminder"] as const) {
    if (!plainObject(local[key]) || !plainObject(merged[key])) continue;
    merged[key] = {
      ...merged[key],
      permission: local[key].permission,
      lastNotifiedDate: local[key].lastNotifiedDate,
    };
  }
  return merged;
}

function rebuildHabitCompletions(state: LegacyState): void {
  const completions: Record<string, Record<string, string>> = {};
  if (Array.isArray(state.habitLogs)) {
    for (const value of state.habitLogs) {
      if (!plainObject(value)) continue;
      const habitId = String(value.habitId ?? value.id ?? "");
      const dateValue = String(value.date ?? "");
      const status = String(value.status ?? "");
      if (
        habitId.length === 0 ||
        dateValue.length === 0 ||
        !["done", "missed", "skipped", "partial"].includes(status)
      ) {
        continue;
      }
      (completions[habitId] ??= {})[dateValue] = status;
    }
  }
  state.habitCompletions = completions;
}

export function applyServerChanges(
  current: LegacyState,
  changes: readonly ServerChange[],
): LegacyState {
  const next = jsonClone(current) as LegacyState;
  const mutableNext: Record<string, unknown> = next;
  next.deleted = plainObject(next.deleted) ? next.deleted : {};

  for (const change of changes) {
    const identity = ARRAY_IDENTITIES[change.collection];
    if (identity !== undefined) {
      const currentRows = mutableNext[change.collection];
      const rows = Array.isArray(currentRows) ? [...currentRows] : [];
      const existingIndex = rows.findIndex(
        (candidate) => plainObject(candidate) && identity(candidate) === change.recordId,
      );
      if (change.deletedAt !== null) {
        if (existingIndex >= 0) rows.splice(existingIndex, 1);
        next.deleted[`${change.collection}:${change.recordId}`] = change.deletedAt;
      } else if (plainObject(change.payload)) {
        let payload = jsonClone(change.payload);
        if (
          change.collection === "progressPhotos" &&
          existingIndex >= 0 &&
          plainObject(rows[existingIndex]) &&
          typeof rows[existingIndex].dataUrl === "string" &&
          plainObject(payload)
        ) {
          payload = { ...payload, dataUrl: rows[existingIndex].dataUrl };
        }
        if (existingIndex >= 0) rows[existingIndex] = payload;
        else rows.push(payload);
        delete next.deleted[`${change.collection}:${change.recordId}`];
      }
      mutableNext[change.collection] = rows;
      continue;
    }

    if (change.recordId !== "singleton" || change.deletedAt !== null) continue;
    if (change.collection === "settings" && plainObject(change.payload)) {
      next.settings = preserveSettingsLocalFields(
        plainObject(next.settings) ? next.settings : {},
        change.payload,
      );
    } else if (change.payload !== null) {
      mutableNext[change.collection] = jsonClone(change.payload);
    }
  }

  rebuildHabitCompletions(next);
  return next;
}
